import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./AgriPage.css";

const API = process.env.REACT_APP_API_URL || "";

const FEATURES = [
    {
        tag: "Mission Planning",
        title: "Plan your spray in seconds.",
        desc: "Draw your field on the map. The app sets the path and waypoints for you automatically.",
        key: "agri_plan",
        stat: { val: "48", label: "Auto Waypoints" },
    },
    {
        tag: "Obstacle Avoidance",
        title: "Avoids obstacles on its own.",
        desc: "Just mark what's in the way. The drone finds a safe path around trees, poles and buildings.",
        key: "agri_fly",
        stat: { val: "3 m", label: "Safe Clearance" },
    },
    {
        tag: "Live Monitoring",
        title: "Watch everything live.",
        desc: "See the video feed, radar and spray data on one screen while the drone is flying.",
        key: "agri_monitor",
        stat: { val: "1 Hz", label: "Update Rate" },
    },
    {
        tag: "Flight Review",
        title: "Review any flight later.",
        desc: "Go back and replay any mission. Check what was covered, how fast, and how much was sprayed.",
        key: "agri_review",
        stat: { val: "100%", label: "Log Accuracy" },
    },
];

function useCountUp(target, duration, active) {
    const [val, setVal] = useState(0);
    useEffect(() => {
        if (!active) return;
        let start = 0;
        const step = target / (duration / 16);
        const t = setInterval(() => {
            start += step;
            if (start >= target) { setVal(target); clearInterval(t); }
            else setVal(Math.floor(start));
        }, 16);
        return () => clearInterval(t);
    }, [active, target, duration]);
    return val;
}

function HeroStats() {
    const ref = useRef(null);
    const [on, setOn] = useState(false);
    useEffect(() => {
        const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) setOn(true); }, { threshold: 0.5 });
        if (ref.current) o.observe(ref.current);
        return () => o.disconnect();
    }, []);
    const a = useCountUp(28, 1200, on);
    const b = useCountUp(100, 1400, on);
    const c = useCountUp(18, 1000, on);
    return (
        <div ref={ref} className="ah-stats">
            <div className="ah-stat"><span className="ah-stat-val agri-mono">{a}</span><span className="ah-stat-lbl">Waypoints</span></div>
            <div className="ah-stat-div" />
            <div className="ah-stat"><span className="ah-stat-val agri-mono">{b}%</span><span className="ah-stat-lbl">Auto Coverage</span></div>
            <div className="ah-stat-div" />
            <div className="ah-stat"><span className="ah-stat-val agri-mono">{c} KPH</span><span className="ah-stat-lbl">Mission Speed</span></div>
        </div>
    );
}

function FeatureCard({ f, index, mediaUrl, isVisible, observerRef }) {
    const cardRef = useRef(null);

    useEffect(() => {
        if (observerRef) observerRef(cardRef.current, index);
    }, [index, observerRef]);

    const handleMouseMove = (e) => {
        const card = cardRef.current;
        if (!card) return;
        const rect = card.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const shimmer = card.querySelector(".fc-img-shimmer");
        if (shimmer) {
            shimmer.style.background = `radial-gradient(circle at ${x}px ${y - rect.height * 0.55}px, rgba(255,255,255,0.12), transparent 60%)`;
        }
    };

    const handleMouseLeave = () => {
        const card = cardRef.current;
        if (!card) return;
        const shimmer = card.querySelector(".fc-img-shimmer");
        if (shimmer) shimmer.style.background = "";
    };

    return (
        <div
            ref={cardRef}
            className={`fc-card ${isVisible ? "fc-card-in" : ""}`}
            style={{ transitionDelay: `${index * 0.1}s` }}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
        >
            <div className="fc-img-wrap">
                {mediaUrl
                    ? <img src={mediaUrl} alt={f.tag} className="fc-img" />
                    : (
                        <div className="fc-img-empty">
                            <span className="agri-mono" style={{ color: "#16a34a", fontSize: 11 }}>{f.key}</span>
                            <span style={{ fontSize: 10, color: "#9ca3af", marginTop: 4 }}>Upload via Media Manager</span>
                        </div>
                    )
                }
                <div className="fc-img-shimmer" />
                <div className="fc-stat-chip agri-mono">
                    <span className="fc-stat-val">{f.stat.val}</span>
                    <span className="fc-stat-label">{f.stat.label}</span>
                </div>
            </div>
            <div className="fc-body">
                <span className="fc-tag agri-mono">{f.tag}</span>
                <h3 className="fc-card-title">{f.title}</h3>
                <p className="fc-desc">{f.desc}</p>
                <span className="fc-arrow agri-mono">Explore →</span>
            </div>
        </div>
    );
}

export default function AgriPage() {
    const [media, setMedia] = useState({});
    const [textIn, setTextIn] = useState(false);
    const [visible, setVisible] = useState([]);
    const observerMap = useRef({});

    useEffect(() => {
        document.title = "Drone Commander GCS - Precision Agriculture | Aviatricks";
        let metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) {
            metaDesc.setAttribute("content", "Elevate agriculture operations with precision spraying and field mapping. Optimize crop health monitoring and field boundary mapping.");
        }

        fetch(`${API}/api/media/agri`)
            .then(r => r.json())
            .then(d => { setMedia(d); })
            .catch(() => { });
        setTimeout(() => setTextIn(true), 400);
    }, []);

    const attachObserver = (el, i) => {
        if (!el || observerMap.current[i]) return;
        const o = new IntersectionObserver(
            ([e]) => { if (e.isIntersecting) setVisible(v => [...new Set([...v, i])]); },
            { threshold: 0.12 }
        );
        o.observe(el);
        observerMap.current[i] = o;
    };

    return (
        <div className="agri-page">

            {/* ── HERO ── */}
            <section className="ah-hero">
                {media.agri_hero_bg && (
                    <img src={media.agri_hero_bg} alt="" className="ah-bg" />
                )}
                <div className="ah-overlay" />
                <div className="ah-scanlines" />
                <div className="ah-frame ah-tl" /><div className="ah-frame ah-tr" />
                <div className="ah-frame ah-bl" /><div className="ah-frame ah-br" />
                <div className="ah-topbar agri-mono">
                    <div className="ah-topbar-l"><span className="ah-live" />DRONE COMMANDER GCS — AGRI</div>
                    <div className="ah-topbar-c">LAT/LON: 52.3456 N, 1.6789 E &nbsp;|&nbsp; ALT: 120M &nbsp;|&nbsp; SPD: 18 KPH</div>
                    <div className="ah-topbar-r">BATT: 68% &nbsp;|&nbsp; CAM: ACTIVE</div>
                </div>
                <div className="ah-botbar agri-mono">
                    <span>WAYPOINTS 1-28 &nbsp;·&nbsp; TRACKING</span>
                    <span>4K &nbsp;·&nbsp; GPS LOCKED &nbsp;·&nbsp; RTK ON</span>
                </div>
                <div className={`ah-content ${textIn ? "ah-in" : ""}`}>
                    <div className="ah-eyebrow agri-mono">
                        <span className="ah-badge">AGRI</span>
                        DroneCommander GCS — Agri
                    </div>
                    <h1 className="ah-h1">
                        <span className="ah-line ah-l1">Fly smarter.</span>
                        <span className="ah-line ah-l2">Spray better.</span>
                    </h1>
                    <p className="ah-p">A simple app to plan, fly and track your agricultural drone missions.<br />Made for Indian farmers.</p>
                    <div className="ah-btns">
                        <a href="#features" className="ah-btn-primary">Explore Features ↓</a>
                        <Link to="/contact/support" className="ah-btn-ghost">Get in Touch</Link>
                    </div>
                    <HeroStats />
                </div>
                <div className="ah-scroll agri-mono">scroll to explore</div>
            </section>

            {/* ── FEATURE CARDS ── */}
            <section className="fc-section" id="features">
                {/* Grid lines — same as eco-bg-grid */}
                <div className="agri-bg-grid" />

                <div className="fc-container">
                    <div className="fc-header">
                        <span className="fc-label agri-mono">CAPABILITIES</span>
                        <h2 className="fc-title">Simple to use.<br />Powerful in the field.</h2>
                        <p className="fc-sub">Plan missions, avoid obstacles, monitor live, and review every flight — all from one app.</p>
                    </div>
                    <div className="fc-grid">
                        {FEATURES.map((f, i) => (
                            <FeatureCard
                                key={f.key}
                                f={f}
                                index={i}
                                mediaUrl={media[f.key]}
                                isVisible={visible.includes(i)}
                                observerRef={attachObserver}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* ── CTA ── */}
            <section className="fc-cta">
                {/* Grid lines — same as eco-bg-grid */}
                <div className="agri-bg-grid" />

                <div className="fc-cta-inner">
                    <span className="agri-mono fc-cta-label">DroneCommander GCS — Agri</span>
                    <h2 className="fc-cta-title">Ready to fly<br />smarter?</h2>
                    <p className="fc-cta-tagline">Precision agriculture, elevated.</p>
                    <p className="fc-cta-p">Get in touch and we'll set you up with DroneCommander GCS for your farm.</p>
                    <Link to="/contact/support" className="ah-btn-primary" style={{ fontSize: 15, padding: "15px 36px" }}>Request a Demo</Link>
                </div>
            </section>

        </div>
    );
}